import { AI_REVIEWED_UNLOCK_STATUSES } from 'constants/class-management.constant';
import { UNLOCK_REQUEST_STATUS } from 'enums';
import {
  AiReviewedUnlockStatus,
  ClassFocusSession,
  ClassStudentDisplayFields,
  ClassUnlockRequest,
  UnlockRequestsByResolution
} from 'interfaces/classes.interface';
import { hasSingleItem } from 'utils/array.util';
import { resolveStudentDisplayName } from 'utils/class-management.util';

export interface UnlockRequestStudentGroup {
  student_id: string;
  name: string;
  requests: ClassUnlockRequest[];
}

export interface PartitionedUnlockRequests {
  groups: UnlockRequestStudentGroup[];
  singles: ClassUnlockRequest[];
}

const collectRequestsByStudent = (
  requests: ClassUnlockRequest[]
): Map<string, ClassUnlockRequest[]> => {
  const byStudentId = new Map<string, ClassUnlockRequest[]>();

  for (const request of requests) {
    const existing = byStudentId.get(request.student_id);
    if (existing) {
      existing.push(request);
    } else {
      byStudentId.set(request.student_id, [request]);
    }
  }

  return byStudentId;
};

const toStudentGroup = (
  studentId: string,
  studentRequests: ClassUnlockRequest[],
  rosterById: Map<string, ClassStudentDisplayFields>
): UnlockRequestStudentGroup => ({
  student_id: studentId,
  name: resolveStudentDisplayName(studentRequests[0], rosterById),
  requests: studentRequests
});

const compareGroupsByCountThenName = (
  a: UnlockRequestStudentGroup,
  b: UnlockRequestStudentGroup
): number =>
  b.requests.length - a.requests.length || a.name.localeCompare(b.name);

/**
 * Students with more than one pending request become collapsible groups
 * (spammer first). Students with a single request stay as flat cards.
 */
export const partitionUnlockRequestsByStudent = (
  requests: ClassUnlockRequest[],
  rosterById: Map<string, ClassStudentDisplayFields>
): PartitionedUnlockRequests => {
  const groups: UnlockRequestStudentGroup[] = [];
  const singles: ClassUnlockRequest[] = [];

  for (const [studentId, studentRequests] of collectRequestsByStudent(
    requests
  )) {
    if (hasSingleItem(studentRequests)) {
      singles.push(studentRequests[0]);
    } else {
      groups.push(toStudentGroup(studentId, studentRequests, rosterById));
    }
  }

  groups.sort(compareGroupsByCountThenName);
  return { groups, singles };
};

export const isAiReviewedUnlockStatus = (
  status: string
): status is AiReviewedUnlockStatus =>
  AI_REVIEWED_UNLOCK_STATUSES.some((aiStatus) => aiStatus === status);

/** Only PENDING awaits the teacher; AI-reviewed rows arrive already resolved. */
export const splitUnlockRequestsByResolution = (
  requests: ClassUnlockRequest[]
): UnlockRequestsByResolution => {
  const pendingRequests: ClassUnlockRequest[] = [];
  const resolvedRequests: ClassUnlockRequest[] = [];
  for (const request of requests) {
    if (request.status === UNLOCK_REQUEST_STATUS.PENDING) {
      pendingRequests.push(request);
    } else {
      resolvedRequests.push(request);
    }
  }
  return { pendingRequests, resolvedRequests };
};

/**
 * Applies a live `class-unlock-requested` row: new ids go on top; a known id is
 * only replaced when the event carries a resolved status, so a late duplicate
 * PENDING event can't reopen a request the teacher already resolved.
 */
export const upsertLiveUnlockRequest = (
  requests: ClassUnlockRequest[],
  incoming: ClassUnlockRequest
): ClassUnlockRequest[] => {
  const existingIndex = requests.findIndex(
    (request) => request.id === incoming.id
  );
  if (existingIndex === -1) {
    return [incoming, ...requests];
  }
  if (incoming.status === UNLOCK_REQUEST_STATUS.PENDING) {
    return requests;
  }
  return requests.map((request, index) =>
    index === existingIndex ? { ...request, ...incoming } : request
  );
};

/** Sessions (and events) from before the backend rollout omit the flag: ON. */
export const isSessionAiContestEnabled = (
  session: Pick<ClassFocusSession, 'ai_contest_enabled'>
): boolean => session.ai_contest_enabled !== false;
